import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";
import ERP from "@apiV2/ERP.mjs";
import Schema from "@apiV2/Schema.mjs";
import View from "@apiV2/View.mjs";

const finPayService = new EntityService("FinPay");

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const isPossivelId = (valor) => {
  if (valor === null || valor === undefined) return false;
  const texto = String(valor).trim();
  return /^[0-9a-f]{24}$/i.test(texto) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto);
};

const primeiroValorValido = (candidatos = []) => {
  for (const valor of candidatos) {
    if (valor !== null && valor !== undefined && String(valor).trim() !== "") {
      return valor;
    }
  }
  return null;
};

const resolverNumeroParcela = (linha = {}) => {
  const direto = primeiroValorValido([
    linha.numParcelas,
    linha.numeroParcela,
    linha.parcelaNumero,
    linha.numeroDaParcela,
    linha.installmentNumber,
    linha.parcela,
    linha.pedidoDeCompraParcelaNumero,
    linha.pedidoDeCompraParcelaIndex,
    linha.parcelaSequencia,
    linha.parcelaOrdem
  ]);

  if (Array.isArray(linha.pedidoDeCompraParcela) && linha.pedidoDeCompraParcela.length > 0) {
    return String(linha.pedidoDeCompraParcela.length);
  }

  if (isPossivelId(direto)) {
    return "-";
  }

  return direto !== null ? String(direto) : "-";
};

const resolverStatusBaixa = (linha = {}) => {
  const statusDireto = primeiroValorValido([
    linha.status,
    linha.situacao,
    linha.statusPagamento,
    linha.pagamentoStatus
  ]);

  if (statusDireto !== null) return String(statusDireto);

  const baixadoFlag = linha.baixado === true || linha.isPaid === true || linha.pago === true;
  const possuiDataPagamento = Boolean(linha.dataPagamento || linha.paidAt);
  if (baixadoFlag || possuiDataPagamento) return "baixado";

  return "não baixado";
};

const normalizarLinha = (linha = {}) => {
  return {
    empresa: linha.filialNome || linha.empresa || "-",
    pedido: linha.pedidoDeCompraCodigo || linha.pedido || "-",
    ordemPagamento: linha.ordemDePagamentoCodigo || linha.ordemPagamento || "-",
    job: linha.job || linha.jobCode || "-",
    os: linha.os || linha.ordemServico || "-",
    centroCusto: linha.centroCusto || "-",
    grupoDesp: linha.grupoDesp || linha.origem || "-",
    numeroNotaFiscal: linha.numeroNotaFiscal || linha.numero || linha.notaNumero || "-",
    dataEmissao: linha.dataEmissao || linha.createAt || null,
    vencimento: linha.dataVencimento || linha.vencimento || null,
    numParcelas: resolverNumeroParcela(linha),
    valorParcela: toNumber(linha.valorPagamento || linha.valorParcela || 0),
    valorTotalNota: toNumber(linha.valorOriginal || linha.valorTotal || 0),
    remetente: linha.favorecidoFornecedorNome || linha.fornecedorNome || "-",
    cnpjEmitente: linha.favorecidoFornecedorCnpj || linha.emit_cnpj || "-",
    status: resolverStatusBaixa(linha),
    cfop: linha.cfop || "-",
    naturezaOperacao: linha.naturezaOperacao || linha.pagamentoTipo || linha.origem || "-",
    infoComplementares: linha.descricao || "-",
    forma: linha.pagamentoForma || "-",
    tipoPagamento: linha.pagamentoTipo || "-",
    evento: linha.pagamentoEvento || "-"
  };
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data);
};

const unicos = (valores = []) =>
  Array.from(new Set(valores.filter((valor) => valor !== null && valor !== undefined && String(valor).trim() !== "")));

const emLotes = (lista = [], tamanho = 40) => {
  const lotes = [];
  for (let i = 0; i < lista.length; i += tamanho) {
    lotes.push(lista.slice(i, i + tamanho));
  }
  return lotes;
};

// GET .../search-fields?ids=... com centenas de ids gera uma URL enorme, que o navegador
// (ou um proxy no meio do caminho) recusa a enviar ("Failed to fetch" sem nem chegar ao
// servidor). Por isso toda busca em lote aqui é quebrada em pedaços menores.
const getManyByIdEmLotes = async (schema, ids = []) => {
  const resultado = [];
  for (const lote of emLotes(ids)) {
    const resposta = await ERP.getManyById(schema, lote);
    if (resposta?.status === 200 && Array.isArray(resposta.data)) {
      resultado.push(...resposta.data);
    }
  }
  return resultado;
};

const getManyByValueEmLotes = async (schema, campo, valores = []) => {
  const resultado = [];
  for (const lote of emLotes(valores)) {
    const resposta = await ERP.getManyByValue(schema, campo, lote);
    if (resposta?.status === 200 && Array.isArray(resposta.data)) {
      resultado.push(...resposta.data);
    }
  }
  return resultado;
};

// FIX (adição JOB/OS/Fornecedor no Relatório de Contas a Pagar):
// O registro bruto de FinPay não traz job/OS diretamente. Existem 2 formatos de linha,
// conforme o campo "origem":
//  - "Ordem de Pagamento": tem o id em linha.ordemDePagamento. O registro de OrdemDePagamento
//    grava diretamente os campos job/ordemDeServico (ver OrdemDePagamentoService.js).
//  - "Pedido de Compra": tem o id em linha.pedidoDeCompra. É preciso encadear
//    PedidoDeCompra.itens (array de ids) -> PedidoDeCompraItens.ordemDeServicoItem ->
//    OrdemDeServico (cuja array "itens" contém o id do item), que só então tem o campo job.
// Todas as buscas abaixo são feitas em lote (nunca 1 request por linha) para não gerar
// centenas de chamadas. Enriquecimento é best-effort: se qualquer etapa falhar, as linhas
// simplesmente continuam mostrando "-" (comportamento atual), sem quebrar o relatório.
const enriquecerJobEOs = async (lista = []) => {
  const jobIdsColetados = new Set();
  const osIdsColetados = new Set();
  const jobEOsPorLinha = new Map();

  try {
    // ---- Caminho 1: FinPay (Ordem de Pagamento) -> OrdemDePagamento.job / .ordemDeServico ----
    const linhasOrdemDePagamento = lista.filter((linha) => linha.origem === "Ordem de Pagamento" && linha.ordemDePagamento);
    const idsOrdemDePagamento = unicos(linhasOrdemDePagamento.map((linha) => linha.ordemDePagamento));

    let mapaOrdemDePagamento = {};
    if (idsOrdemDePagamento.length) {
      const registros = await getManyByIdEmLotes(Schema.ORDEM_DE_PAGAMENTO, idsOrdemDePagamento);
      registros.forEach((registro) => { mapaOrdemDePagamento[registro.id] = registro; });
    }

    linhasOrdemDePagamento.forEach((linha) => {
      const registro = mapaOrdemDePagamento[linha.ordemDePagamento];
      if (!registro) return;
      if (registro.job) jobIdsColetados.add(registro.job);
      if (registro.ordemDeServico) osIdsColetados.add(registro.ordemDeServico);
      jobEOsPorLinha.set(linha, { jobId: registro.job, osId: registro.ordemDeServico });
    });

    // ---- Caminho 2: FinPay (Pedido de Compra) -> Itens -> OrdemDeServicoItem -> OrdemDeServico.job ----
    const linhasPedidoDeCompra = lista.filter((linha) => linha.origem === "Pedido de Compra" && linha.pedidoDeCompra);
    const idsPedidoDeCompra = unicos(linhasPedidoDeCompra.map((linha) => linha.pedidoDeCompra));

    const mapaPedidoParaOS = {};
    if (idsPedidoDeCompra.length) {
      // PedidoDeCompraItens não tem um campo "pedidoDeCompra" para busca reversa (mesmo
      // padrão do OrdemDeServicoItens: só o pai guarda a lista de ids dos filhos, em
      // PedidoDeCompra.itens). Por isso primeiro buscamos os PedidoDeCompra pelos ids
      // vindos do FinPay, e usamos o array .itens de cada um (ver criarPedido/
      // cancelarPedido em PedidoDeCompraService.js, que usa exatamente esse caminho).
      const pedidos = await getManyByIdEmLotes(Schema.PEDIDO_DE_COMPRA, idsPedidoDeCompra);

      const itemIdsPorPedido = {};
      const todosItensDoPedidoIds = new Set();

      pedidos.forEach((pedido) => {
        const itensDoPedido = Array.isArray(pedido.itens) ? pedido.itens : [];
        itemIdsPorPedido[pedido.id] = itensDoPedido;
        itensDoPedido.forEach((itemId) => todosItensDoPedidoIds.add(itemId));
      });

      // Cada PedidoDeCompraItens tem o campo ordemDeServicoItem apontando pro item da OS.
      const pedidoDeCompraItens = await getManyByIdEmLotes(Schema.PEDIDO_DE_COMPRA_ITENS, Array.from(todosItensDoPedidoIds));
      const ordemDeServicoItemPorItemDoPedido = {};
      pedidoDeCompraItens.forEach((item) => { ordemDeServicoItemPorItemDoPedido[item.id] = item.ordemDeServicoItem; });

      Object.keys(itemIdsPorPedido).forEach((pedidoId) => {
        itemIdsPorPedido[pedidoId] = itemIdsPorPedido[pedidoId]
          .map((itemId) => ordemDeServicoItemPorItemDoPedido[itemId])
          .filter(Boolean);
      });

      const todosOsItemIds = new Set(Object.values(itemIdsPorPedido).flat());

      if (todosOsItemIds.size) {
        const ordensDeServico = await getManyByValueEmLotes(Schema.ORDEM_DE_SERVICO, "itens", Array.from(todosOsItemIds));
        Object.entries(itemIdsPorPedido).forEach(([pedidoId, itemIds]) => {
          const osEncontrada = ordensDeServico.find(
            (os) => Array.isArray(os.itens) && os.itens.some((itemId) => itemIds.includes(itemId))
          );
          if (osEncontrada) {
            mapaPedidoParaOS[pedidoId] = osEncontrada;
            if (osEncontrada.job) jobIdsColetados.add(osEncontrada.job);
            osIdsColetados.add(osEncontrada.id);
          }
        });
      }
    }

    linhasPedidoDeCompra.forEach((linha) => {
      const osEncontrada = mapaPedidoParaOS[linha.pedidoDeCompra];
      if (!osEncontrada) return;
      jobEOsPorLinha.set(linha, { jobId: osEncontrada.job, osId: osEncontrada.id });
    });

    // ---- Resolve códigos legíveis de Job e OS em lote ----
    let mapaJobCodigo = {};
    if (jobIdsColetados.size) {
      const jobs = await getManyByIdEmLotes(Schema.JOB, Array.from(jobIdsColetados));
      jobs.forEach((job) => { mapaJobCodigo[job.id] = job.codigo || job.code || job.numero || ""; });
    }

    let mapaOsCodigo = {};
    if (osIdsColetados.size) {
      const ordensDeServico = await getManyByIdEmLotes(Schema.ORDEM_DE_SERVICO, Array.from(osIdsColetados));
      ordensDeServico.forEach((os) => { mapaOsCodigo[os.id] = os.codigo || ""; });
    }

    // ---- Aplica nas linhas originais (a normalizarLinha já lê linha.job/linha.os) ----
    jobEOsPorLinha.forEach((valores, linha) => {
      if (valores.jobId && mapaJobCodigo[valores.jobId]) linha.job = mapaJobCodigo[valores.jobId];
      if (valores.osId && mapaOsCodigo[valores.osId]) linha.os = mapaOsCodigo[valores.osId];
    });
  } catch (e) {
    // Enriquecimento é best-effort: nunca deve quebrar o relatório.
  }

  return lista;
};

// FIX (CNPJ do Emitente = CNPJ do Fornecedor, ou CPF quando o credor é Colaborador):
// A view "contas-a-pagar" só devolve grupo.credor.{id,tipo,codigo,nome} -- sem CNPJ/CPF. O
// credor de uma Ordem de Pagamento pode ser um Fornecedor (Schema.FORNECEDOR, campo "cnpj"
// ou "cpf" se for PF) ou um Colaborador (Schema.COLABORADOR = "Collaborator", só tem "cpf" --
// caso de reembolso/adiantamento pago direto a um funcionário, ex.: OPs do "Fernando"). Em
// vez de buscar 1 por linha (centenas de requests), reaproveita o padrão de enriquecerJobEOs:
// coleta os ids únicos de cada tipo e faz poucas chamadas em lote (getManyByIdEmLotes, já em
// lotes de 40 ids por request). Best-effort: se falhar, linha continua mostrando "-".
const enriquecerCnpjFornecedor = async (lista = []) => {
  try {
    const idsFornecedor = unicos(lista.map((linha) => linha.favorecidoFornecedor));
    const idsColaborador = unicos(lista.map((linha) => linha.favorecidoColaborador));

    const [fornecedores, colaboradores] = await Promise.all([
      idsFornecedor.length ? getManyByIdEmLotes(Schema.FORNECEDOR, idsFornecedor) : [],
      idsColaborador.length ? getManyByIdEmLotes(Schema.COLABORADOR, idsColaborador) : []
    ]);

    const mapaDocumentoPorFornecedor = {};
    // Pessoa Jurídica tem cnpj; Pessoa Física tem cpf -- a coluna do relatório mostra
    // qualquer um dos dois que existir (ver rótulo "CNPJ / CPF" na view).
    fornecedores.forEach((fornecedor) => { mapaDocumentoPorFornecedor[fornecedor.id] = fornecedor.cnpj || fornecedor.cpf; });

    const mapaDocumentoPorColaborador = {};
    colaboradores.forEach((colaborador) => { mapaDocumentoPorColaborador[colaborador.id] = colaborador.cpf; });

    lista.forEach((linha) => {
      const documento =
        (linha.favorecidoFornecedor && mapaDocumentoPorFornecedor[linha.favorecidoFornecedor]) ||
        (linha.favorecidoColaborador && mapaDocumentoPorColaborador[linha.favorecidoColaborador]);
      if (documento) linha.favorecidoFornecedorCnpj = documento;
    });
  } catch (e) {
    // Enriquecimento é best-effort: nunca deve quebrar o relatório.
  }

  return lista;
};

const listarPorEntidade = async () => {
  const response = await finPayService.getAll(1, 5000, "", "", null, null, null, "dataVencimento");
  return extrairLista(response);
};

// PERFORMANCE (view "contas-a-pagar-relatorio-new"): essa view agrega direto em cima de
// "finpay" e já devolve o CNPJ/CPF do credor E o JOB/OS resolvidos dentro do próprio MongoDB
// (ver View.mjs / pipeline no Designer), então quando ela é usada como fonte tanto
// enriquecerCnpjFornecedor quanto enriquecerJobEOs ficam completamente dispensados -- o
// carregamento cai de ~11-16s para ~1s (validado em HML: 121/124 linhas "Pedido de Compra" e
// 10/280 "Ordem de Pagamento" vieram com job/os preenchidos -- o resto é o esperado: OP sem
// job vinculado, ex. despesas gerais, e casos raros de item sem OS). Se a view ainda não
// existir neste ambiente (retorna vazio), cai automaticamente no caminho legado abaixo.
const listarPorViewNova = async () => {
  const resposta = await ERP.getManyWithView(View.CONTAS_A_PAGAR_RELATORIO_NEW);
  if (resposta?.status !== 200 || !Array.isArray(resposta?.data)) return [];
  return resposta.data;
};

// FIX (espelhar #/tela/contas-a-pagar no relatório):
// A tela real usa a view "contas-a-pagar" (ERP.getManyWithView(View.CONTAS_A_PAGAR)), que já
// vem com credor, forma/tipo/evento de pagamento, liberador/aprovador/reprovador e a nota fiscal
// recebida vinculada ao pagamento -- nada disso existe no Entity/FinPay bruto que o relatório
// usava antes. O retorno é aninhado (grupo Ordem de Pagamento/Pedido de Compra -> parcelas[] ->
// contaAPagar -> pagamentos[]); achatarGruposDaView() transforma isso em 1 linha por pagamento
// (ou 1 linha por contaAPagar quando ainda não há pagamento gerado), preservando os nomes de
// campo que normalizarLinha()/enriquecerJobEOs() já esperam (origem, ordemDePagamento,
// pedidoDeCompra, etc.) para não duplicar lógica.
const listarPorViewReal = async () => {
  const resposta = await ERP.getManyWithView(View.CONTAS_A_PAGAR);
  if (resposta?.status !== 200 || !Array.isArray(resposta?.data)) return [];
  return resposta.data;
};

const achatarGruposDaView = (grupos = []) => {
  const linhas = [];

  grupos.forEach((grupo) => {
    const ehOrdemDePagamento = grupo.tipo === "Ordem de Pagamento";
    const ehPedidoDeCompra = grupo.tipo === "Pedido de Compra";

    (grupo.parcelas || []).forEach((parcela) => {
      const contaAPagar = parcela.contaAPagar || {};
      const pagamentos = Array.isArray(contaAPagar.pagamentos) ? contaAPagar.pagamentos : [];
      // Sem pagamento gerado ainda: 1 linha só com os dados da contaAPagar (comportamento
      // igual ao que a tela mostra antes de expandir/gerar pagamentos).
      const basePagamentos = pagamentos.length ? pagamentos : [null];

      basePagamentos.forEach((pagamento) => {
        linhas.push({
          origem: contaAPagar.origem || grupo.tipo,
          ordemDePagamento: ehOrdemDePagamento ? grupo.id : null,
          ordemDePagamentoCodigo: ehOrdemDePagamento ? grupo.codigo : null,
          pedidoDeCompra: ehPedidoDeCompra ? grupo.id : null,
          pedidoDeCompraCodigo: ehPedidoDeCompra ? grupo.codigo : null,
          favorecidoFornecedorNome: grupo.credor?.nome || "-",
          // id do Fornecedor OU do Colaborador (a view não traz CNPJ/CPF, só nome/código) --
          // usado só para o enriquecimento em lote de CNPJ/CPF em enriquecerCnpjFornecedor().
          // Credor pode ser um Fornecedor (PJ ou PF com CNPJ/CPF no próprio cadastro) ou um
          // Colaborador (reembolso/adiantamento a funcionário, ex: OP paga pro "Fernando"),
          // cujo CPF fica no cadastro de Collaborator, não no de Fornecedor.
          favorecidoFornecedor: grupo.credor?.tipo === "Fornecedor" ? grupo.credor.id : null,
          favorecidoColaborador: grupo.credor?.tipo === "Colaborador" ? grupo.credor.id : null,
          status: contaAPagar.status || "-",
          descricao: contaAPagar.descricao,
          pagamentoForma: contaAPagar.pagamentoForma,
          pagamentoTipo: contaAPagar.pagamentoTipo,
          pagamentoEvento: contaAPagar.pagamentoEvento,
          liberadorNome: contaAPagar.liberador?.nome,
          aprovadorNome: contaAPagar.aprovador?.nome,
          reprovadorNome: contaAPagar.reprovador?.nome,
          reprovadorJustificativa: contaAPagar.reprovadorJustificativa,
          dataVencimento: contaAPagar.dataVencimento,
          dataEmissao: pagamento?.dataCriacao || contaAPagar.dataVencimento,
          numeroNotaFiscal: pagamento?.notaFiscalRecebida?.numero || null,
          valorOriginal: contaAPagar.valorOriginal,
          valorPagamento: pagamento ? pagamento.valorPagamento : contaAPagar.valorPagamento,
          numParcelas: contaAPagar.pagamentosQuantidade
        });
      });
    });
  });

  return linhas;
};

const listarPorView = async (params = []) => {
  const viewNames = [
    "relatorio-contas-pagar",
    "df2990fb-e9d5-4303-8400-605f89af52ea",
    "finpay"
  ];

  for (const viewName of viewNames) {
    const response = await viewService.viewRun(viewName, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  }

  return [];
};

export default {
  async listarContasAPagar(filtros = {}) {
    const params = [];

    if (filtros.dataInicio) {
      params.push({ dataType: "String", name: "dataInicio", value: filtros.dataInicio });
    }

    if (filtros.dataFim) {
      params.push({ dataType: "String", name: "dataFim", value: filtros.dataFim });
    }

    if (filtros.fornecedor) {
      params.push({ dataType: "String", name: "fornecedor", value: filtros.fornecedor });
    }

    let lista = [];

    // Caminho novo (rápido): já vem achatado, com CNPJ/CPF e JOB/OS resolvidos na própria
    // view -- ver comentário em listarPorViewNova. Enquanto a view não existir neste
    // ambiente, listarPorViewNova() devolve [] e cai no caminho legado abaixo, sem quebrar
    // o relatório.
    lista = await listarPorViewNova();

    if (lista.length) {
      return lista.map((linha) => normalizarLinha(linha));
    }

    // Caminho legado (mais lento): view "contas-a-pagar" aninhada + achatamento no front +
    // enriquecimento de JOB/OS e CNPJ/CPF via chamadas extras em lote.
    const gruposDaViewReal = await listarPorViewReal();
    if (gruposDaViewReal.length) {
      lista = achatarGruposDaView(gruposDaViewReal);
    }

    if (!lista.length) {
      lista = await listarPorView(params);
    }

    if (!lista.length) {
      lista = await listarPorEntidade();
    }

    lista = await enriquecerJobEOs(lista);
    lista = await enriquecerCnpjFornecedor(lista);

    return lista.map((linha) => normalizarLinha(linha));
  }
};
